<template>
    <select-input
        v-if="assistentCollection && !assistentCollection.loading.value"
        :items="assistentCollection.data.value"
        value-key-name="id"
        label-key-name="name"
        class="assistent-select-input"
        :no-scroll="assistentCollection.total.value < 5"
        :collapse-padding="3"
        variant="inline"
    >
        <template #active="{ bindings }">
            <div
                v-if="bindings.value"
                class="assistent-select-input__item-container"
            >
                <div class="assistent-select-input__avatar">
                    <assistent-avatar :assistent="bindings.value" size="xl" />
                    <div
                        class="assistent-select-input__status-dot"
                        :style="{ backgroundColor: statusColor(bindings.value) }"
                    />
                    <div
                        v-if="assistentCounts[bindings.value.id.id]"
                        class="assistent-select-input__number-alert"
                    >
                        {{ assistentCounts[bindings.value.id.id] }}
                    </div>
                </div>
                <div class="assistent-select-input__label">
                    <text-decorator bold>
                        {{ bindings.value.name }}
                    </text-decorator>
                    <text-decorator>
                        {{ bindings.value.brand.websiteDomain ?? bindings.value.brand.name }}
                    </text-decorator>
                </div>
            </div>
        </template>
        <template #item="{ bindings }">
            <div
                v-if="bindings.item"
                class="assistent-select-input__item-container"
            >
                <div class="assistent-select-input__avatar">
                    <assistent-avatar :assistent="bindings.item" size="xl" />
                    <div
                        class="assistent-select-input__status-dot"
                        :style="{ backgroundColor: statusColor(bindings.item) }"
                    />
                    <div
                        v-if="assistentCounts[bindings.item.id.id]"
                        class="assistent-select-input__number-alert"
                    >
                        {{ assistentCounts[bindings.item.id.id] }}
                    </div>
                </div>
                <div class="assistent-select-input__label">
                    <text-decorator bold>
                        {{ bindings.item.name }}
                    </text-decorator>
                    <text-decorator>
                        {{ bindings.item.brand.websiteDomain ?? bindings.item.brand.name }}
                    </text-decorator>
                </div>
            </div>
        </template>
        <template #item-active="{ bindings }">
            <div
                :class="{
                    'assistent-select-input__active-icon': true,
                    'assistent-select-input__active-icon--active': bindings.isSelected,
                }"
            />
        </template>
    </select-input>
    <bar-skeleton
        v-else
        dark
        :width="0"
        :height="70"
    />
</template>

<script setup lang="ts">
    import useClient from '@/services/keyvue-api/composables/useClient';
    import { onMounted, ref } from 'vue';
    import { Assistent, AssistentStatus, CountChatsCommand, type CountChatsCommandResponse } from '@keyvue/one-api-client';
    import { getCssColor, SelectInput } from '@keyvue/one-ui';
    import AssistentAvatar from '@/ai/assistent/components/AssistentAvatar.vue';

    const { client, getLazyResourceCollection } = useClient();
    const assistentCollection = getLazyResourceCollection(Assistent);
    const assistentCounts = ref<Record<string, number>>({});

    onMounted(async () => {
        await loadAssistents();
    });

    async function loadAssistents() {
        await assistentCollection.load({
            'order[name]': 'asc',
        });

        assistentCollection.data.value.forEach((value) => {
            client.dispatch(new CountChatsCommand(value.id)).then(
                (counts: CountChatsCommandResponse) => {
                    assistentCounts.value[value.id.id] = counts.chatCounts.handoversRequired;
                },
            );
        });
    }

    function statusColor(assistent: Assistent) {
        if (!assistent.active) {
            return getCssColor('default', 'red-500');
        }

        switch (assistent.status.value) {
            case AssistentStatus.READY.value:
                return getCssColor('default', 'green-500');
            case AssistentStatus.CRAWLING.value:
            case AssistentStatus.ANALYZING.value:
            case AssistentStatus.GENERATING.value:
                return getCssColor('default', 'orange-500');
            default:
                return getCssColor('default', 'red-500');
        }
    }
</script>

<style scoped lang="scss">
    .assistent-select-input {
        height: 70px !important;
        width: 100% !important;
        border-radius: size(sm) !important;
        padding: 0 size(sm);
        box-sizing: border-box;
        @include box-shadow-360();

        &__avatar {
            position: relative;
            width: size(xl);
            height: size(xl);
        }

        &__item-container {
            display: flex;
            gap: size(sm);
            width: max-content;
            max-width: 100%;
            padding: size(sm) 0;
            min-width: calc(100% - 40px);
        }

        &__label {
            display: flex;
            flex-direction: column;
            align-content: flex-start;
            justify-content: center;
            width: calc(100% - 40px);

            & > span {
                width: 100%;
                white-space: nowrap;
                text-overflow: ellipsis;
                overflow: hidden;
            }
        }

        &__status-dot {
            position: absolute;
            right:0;
            bottom:0;
            height: 8px;
            width: 8px;
            border-radius: 5px;
            border: 1px solid white;
        }

        &__active-icon {
            height: 15px;
            width: 15px;
            box-sizing: border-box;
            border-radius: 8px;
            border: 1px solid var(--theme-colors-default--gray-300);
            margin-left: auto;

            &--active {
                border: 4px solid var(--theme-colors-default--primary);
            }
        }

        &__number-alert {
            position: absolute;
            display: flex;
            align-content: center;
            justify-content: center;
            left: -3px;
            top: -3px;
            height: 12px;
            width: 12px;
            border-radius: 7px;
            border: 1px solid white;
            background-color: var(--theme-colors-default--red-500);
            color: white;
            font-size: font-size(xxs);
        }
    }
</style>
